/* eslint-disable */
import React, { useMemo, useState, useContext, useCallback, useEffect } from "react";
import type { MenuProps } from "antd";
import { Typography, Menu, theme, Button, Tag, ConfigProvider, Flex } from "antd";
import type { ItemData as RawItemData } from "types/layout";
import QLayoutContext from "contexts/Layout";
import { useTranslation } from "react-i18next";
import styles from "styles/layout.scss";
import ReactGA from "react-ga4";
import GAMap from "tokens/GAMap.json";
import { TFunction } from "i18next";
import { tagMap, iconMap, labelMap, qerpLabelMap } from "tokens/layout";
import { PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import CustomerPortalModal from "./CustomerPortalModal";
import QuickCreateModal, { hasQuickCreateActions } from "./QuickCreateModal";
import AiNavEntry from "./AiNavEntry";

const { useToken } = theme;
const { Link } = Typography;

type MenuItem = Required<MenuProps>["items"][number];

const isQerpTheme = () =>
  typeof document !== "undefined" && document.body.classList.contains("qerp-theme");

// QERP overrides the label for a handful of nav items; everyone else keeps labelMap.
export const resolveLabel = (id: string): string =>
  (isQerpTheme() && qerpLabelMap.get(id)) || labelMap.get(id)!;

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  style?: React.CSSProperties,
  clickAction?: () => void,
  type?: "group",
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
    style: { height: "auto", ...style },
    className: styles.q_ant_menu__sub_item,
    onTitleClick: clickAction,
  } as MenuItem;
}

function getItems(
  broken: boolean,
  isMobileRequest: boolean,
  t: TFunction<"translation", undefined>,
  startIntro: () => void,
  setOpenKeys: (ids: string[]) => void,
  rawItem: RawItemData,
  style: React.CSSProperties = {},
): MenuItem {
  if (!rawItem || !rawItem.props) {
    return null;
  }

  const { props, children, new_entity, tag } = rawItem;
  let actionElement = null;
  const { id } = props;
  let { href } = props;

  let showMeHow = () => {};
  if (id === "sidepanel_show_me_how") {
    if (broken) return null;
    showMeHow = startIntro;
  }

  // A rejected GetPaid application has no page to land on, so this entry re-opens the
  // activation drawer instead of navigating. It is the only durable way back in once the
  // rejection banner has been dismissed. See QYDDEV-19813.
  let openGetPaidDrawer: (event: React.MouseEvent<HTMLElement>) => void = () => {};
  if (tag?.type === "rejected") {
    openGetPaidDrawer = (event) => {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent("triggerGetPaidDrawer"));
    };
  }

  const moduleName = id.split("__")?.[1] ?? id.split("_").slice(1).join("_");
  let [path, newPath, dataToggle, dataTarget] = [href, new_entity?.link, "", ""];
  let mobileWarning: (module: string) => void = () => {};

  if (isMobileRequest && href && href !== "#" && !id.startsWith("sidepanel_settings")) {
    [path, newPath, dataToggle, dataTarget] = ["#", "#", "modal", `#${moduleName}`];
    mobileWarning = (module) => window.replaceModuleWords(module);
  }

  let TagElement: () => null | React.ReactNode = () => null;
  if (tag) {
    const tagData = tagMap.get(tag.type)!;
    TagElement = () => <Tag color={tagData.color}>{t(tagData.label)}</Tag>;
  }

  if (new_entity?.allowed) {
    const newClickAction = () => {
      const createAction = (GAMap.sidemenu[id] as { create: string })?.create;
      if (createAction) {
        ReactGA.event({
          category: GAMap.sidemenu.CATEGORY,
          action: createAction,
        });
      }
      mobileWarning(`new_${moduleName}`);
    };

    actionElement = (
      <Button
        type="link"
        href={newPath}
        data-toggle={dataToggle}
        data-target={`#new_${moduleName}`}
        onClick={newClickAction}
        style={{ color: "inherit", display: "inline" }}
        icon={<PlusOutlined />}
      />
    );
  }

  href = href || "#";

  let itemChildren: MenuItem[] | undefined = undefined;
  if (children?.length ?? 0 > 0) {
    itemChildren = [];
    children!.forEach((child) => {
      itemChildren!.push(
        getItems(broken, isMobileRequest, t, startIntro, () => {}, child, {
          display: "flex",
        }),
      );
    });
  }

  const clickAction = (event: React.MouseEvent<HTMLElement>) => {
    const viewAction = (GAMap.sidemenu[id] as { view: string })?.view;
    if (viewAction) {
      ReactGA.event({
        category: GAMap.sidemenu.CATEGORY,
        action: viewAction,
      });
    }
    showMeHow();
    mobileWarning(moduleName);
    openGetPaidDrawer(event);
  };

  const openInNewTab = [
    "sidepanel_knowledge_base",
    "sidepanel_advanced_accounting_portal",
    "sidepanel_customer_portal_demo",
  ];
  return getItem(
    <Flex
      gap={4}
      style={{
        display: "inline-flex",
        justifyContent: "space-between",
        lineHeight: 1.2,
        width: "100%",
      }}
    >
      <Link
        href={path}
        data-toggle={dataToggle}
        data-target={dataTarget}
        onClick={clickAction}
        style={{
          color: "inherit",
          whiteSpace: "break-spaces",
          wordBreak: "unset",
          flex: 1,
        }}
        target={openInNewTab.includes(id) ? "_blank" : "_self"}
      >
        <Flex gap={4} justify="space-between" align="center">
          {t(resolveLabel(id))}
          <TagElement />
        </Flex>
        {id === "sidepanel_customer_portal_demo" && <CustomerPortalModal />}
      </Link>
      {actionElement}
    </Flex>,
    id,
    iconMap.get(id),
    itemChildren,
    style,
    setOpenKeys.bind(null, [id]),
  );
}

interface SideMenuProps {
  startIntro: () => void;
  collapsed: boolean;
  broken: boolean;
}

const SideMenu = ({ startIntro, collapsed, broken }: SideMenuProps) => {
  const { t } = useTranslation();
  const { sideMenuData, currentActiveItem, currentActiveMenu, isMobileRequest, aiToolsEnabled } =
    useContext(QLayoutContext);
  const [openKeys, setOpenKeys] = useState<string[] | undefined>([currentActiveMenu]);
  const [lastOpenedKeys, setLastOpenedKeys] = useState<string[] | undefined>([currentActiveMenu]);

  const { token } = useToken();
  const isQerp = typeof document !== "undefined" && document.body.classList.contains("qerp-theme");
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  const openQuickCreate = useCallback(() => {
    setQuickCreateOpen(true);
    ReactGA.event({
      category: GAMap.sidemenu.CATEGORY,
      action: GAMap.sidemenu.quickCreate.open,
    });
  }, []);

  const openKey = useCallback(
    (keys: string[]) => {
      setOpenKeys((prevState) => (prevState?.[0] === keys[0] ? [] : keys));
    },
    [openKeys],
  );

  useEffect(() => {
    if (collapsed) {
      setLastOpenedKeys(openKeys);
      setOpenKeys(undefined);
    } else {
      setOpenKeys(lastOpenedKeys);
    }
  }, [collapsed]);

  const items = useMemo<MenuItem[]>(
    () =>
      sideMenuData?.map<MenuItem>((rawItem) =>
        getItems(broken, isMobileRequest, t, startIntro, openKey, rawItem),
      ),
    [sideMenuData, isMobileRequest, broken],
  );

  const showQuickCreate = useMemo(() => hasQuickCreateActions(sideMenuData), [sideMenuData]);

  // QERP: group the flat nav into labelled sections; unmapped keys stay ungrouped (nothing disappears).
  const displayItems = useMemo(() => {
    if (!isQerp || !items) return items;
    const groupKey = (name: string) => `activerecord.attributes.layout.groups.${name}`;
    const GROUPS = [
      { label: "", keys: ["sidepanel_dashboards"] },
      { label: groupKey("trading"), keys: ["sidepanel_sales", "sidepanel_delivery_orders"] },
      {
        label: groupKey("procurement"),
        keys: ["sidepanel_purchases", "sidepanel_expense_claims", "sidepanel_purchase_requests"],
      },
      { label: groupKey("inventory_management"), keys: ["sidepanel_products"] },
      { label: groupKey("hr_payroll"), keys: ["sidepanel_payrolls"] },
      {
        label: groupKey("financial_management"),
        keys: ["sidepanel_accounting", "sidepanel_fixed_assets"],
      },
      {
        label: groupKey("projects_services"),
        keys: ["sidepanel_projects_tasks", "sidepanel_qoyod_services"],
      },
      { label: groupKey("insights"), keys: ["sidepanel_reports"] },
      {
        label: groupKey("system"),
        keys: ["sidepanel_settings", "sidepanel_show_me_how", "sidepanel_knowledge_base"],
      },
    ];
    const byKey = new Map(items.map((it) => [String(it && it.key), it]));
    const used = new Set();
    const out: MenuItem[] = [];
    GROUPS.forEach((g, gi) => {
      const children = g.keys.map((k) => byKey.get(k)).filter(Boolean) as MenuItem[];
      children.forEach((c) => used.add(String((c as { key?: unknown }).key)));
      if (!children.length) return;
      if (g.label) out.push({ type: "group", key: `qerp-grp-${gi}`, label: t(g.label), children });
      else children.forEach((c) => out.push(c));
    });
    items.forEach((it) => {
      if (it && !used.has(String((it as { key?: unknown }).key))) out.push(it);
    });
    return out;
  }, [items, isQerp, t]);

  return (
    <>
      {showQuickCreate && (
        <div style={{ padding: token.paddingSM }}>
          <Button
            block
            icon={<ThunderboltOutlined />}
            onClick={openQuickCreate}
            style={{
              fontWeight: 600,
              gap: token.paddingXXS,
              ...(isQerp
                ? {
                    background: token.colorLink,
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    height: 40,
                    boxShadow: "0 2px 8px rgba(37,99,235,.35)",
                  }
                : {}),
            }}
          >
            {collapsed ? null : t("activerecord.attributes.layout.quick_create_title")}
          </Button>
        </div>
      )}
      {aiToolsEnabled && (
        <div
          style={{
            padding: token.paddingSM,
            paddingTop: showQuickCreate ? 0 : token.paddingSM,
          }}
        >
          <AiNavEntry collapsed={collapsed} />
        </div>
      )}
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              darkItemBg: isQerp ? "#0F172A" : token.colorPrimary,
              // QERP: submenu blends into the sidebar; the active item pops in accent blue.
              darkSubMenuItemBg: isQerp ? "#0F172A" : token.colorInfo,
              darkItemSelectedBg: isQerp ? token.colorLink : token.colorInfo,
              // QERP (Ink Slate): near-black nav, white items, rounded "pill" active item.
              ...(isQerp
                ? {
                    itemBorderRadius: 10,
                    subMenuItemBorderRadius: 8,
                    itemMarginInline: 12,
                    itemMarginBlock: 4,
                    itemHeight: 42,
                    iconSize: 17,
                    collapsedIconSize: 18,
                    darkItemHoverBg: "rgba(255,255,255,0.06)",
                    darkItemColor: "rgba(255,255,255,0.72)",
                    darkItemSelectedColor: "#ffffff",
                    darkSubMenuItemBg: "transparent",
                    groupTitleColor: "rgba(255,255,255,0.42)",
                    groupTitleFontSize: 10,
                  }
                : {}),
            },
          },
        }}
      >
        <Menu
          items={displayItems}
          style={{ width: "100%" }}
          theme="dark"
          openKeys={openKeys}
          mode="inline"
          selectedKeys={[currentActiveItem]}
        />
      </ConfigProvider>
      {showQuickCreate && (
        <QuickCreateModal open={quickCreateOpen} onClose={() => setQuickCreateOpen(false)} />
      )}
    </>
  );
};
/* eslint-enable */

export default SideMenu;
